<template><div><h2 id="active-voice" tabindex="-1" class="d-docsite--header-2">Active Voice <a class="header-anchor d-link d-docsite--link d-link" href="#active-voice" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We help users accomplish tasks every day. To reflect that acitvity, we write using an active voice in our copy. Use this voice for when the user needs to do something. We only allow a passive voice in very specific scenarios. Otherwise, use an active voice in all copy you write.</p>
<p class="d-docsite--paragraph">To write in an active voice, you place the subject at the start of a sentence. It clearly identifies who is performing the action. This results in more direct and concise writing. It should be clear that the subject is the one doing the action.</p>
<blockquote>
<p class="d-docsite--paragraph">Subject (Person / Thing) + Verb (the action) + Object (What receives the action)</p>
</blockquote>
<dialtone-usage>
<template #do>
<p class="d-docsite--paragraph">Calls will be sent to voicemail.</p>
</template>
<template #dont>
<p class="d-docsite--paragraph">Missed calls were sent to voicemail.</p>
</template>
</dialtone-usage>
<h2 id="passive-voice" tabindex="-1" class="d-docsite--header-2">Passive Voice <a class="header-anchor d-link d-docsite--link d-link" href="#passive-voice" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Try to catch if you're using a passive voice by looking out for the following signs:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">When the object becomes the subject (“the call was made by the admin”)</li>
<li class="d-docsite--list-element">Using past tense verbs (“was called”, “was messaged”)</li>
<li class="d-docsite--list-element">Using “to be” verbs (“was”, “is”, “were”)</li>
</ul>
<p class="d-docsite--paragraph">There are times when passive voice is permissible:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">To avoid referring to yourself or Dialpad</li>
<li class="d-docsite--list-element">To make it clear that you didn’t personally take an action or make a decision</li>
<li class="d-docsite--list-element">If the object is more important than the subject</li>
</ul>
<h2 id="sentence-vs-title-casing" tabindex="-1" class="d-docsite--header-2">Sentence vs. Title Casing <a class="header-anchor d-link d-docsite--link d-link" href="#sentence-vs-title-casing" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Dialpad uses sentence casing for all writing except action-oriented areas (e.g., menus, buttons, links). In those situations, we use title casing.</p>
<p class="d-docsite--paragraph">Sentence casing aligns with our more casual brand language. There are times though when being too casual can get in the way. Wayfinding and actions are key moments within Dialpad where it’s important to be more straight-forward and formal.</p>
<p class="d-docsite--paragraph">When writing in sentence casing, capitalize the first word of the sentence and proper nouns only. For title casing, use the following rules:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The first word of a title or heading, even if it’s a minor word such as “The” or “A”</li>
<li class="d-docsite--list-element">The first word after a color, em dash, or end punctuation in a heading</li>
<li class="d-docsite--list-element">Major words, including the second part of hyphenated major words (e.g., “Self-Report”, not “Self-report”)</li>
<li class="d-docsite--list-element">Words of four letters or more (e.g., “With”, “Between”, “From”)</li>
<li class="d-docsite--list-element">Lowercase only minor words that are three letters or fewer in a title or heading (except in rules outlined above):
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Short conjunctions (e.g., “and”, “as”, “but”, “for”, “if”, “so”)</li>
<li class="d-docsite--list-element">Articles (“a”, “an”, “the”)</li>
<li class="d-docsite--list-element">Short prepositions (e.g., “as”, “at”, “for”, “in”, “off”, “of”, “on”, “to”)</li>
</ul>
</li>
</ul>
<h2 id="contractions" tabindex="-1" class="d-docsite--header-2">Contractions <a class="header-anchor d-link d-docsite--link d-link" href="#contractions" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Contractions are abbreviated words (e.g., can’t”, “aren’t”, and “you’re”). Their use helps us sound human and approachable in our writing. While you are free to use contractions in most cases, avoid using them in the following scenarios:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">They sound awkward when you say them out loud</li>
<li class="d-docsite--list-element">They’re no longer regularly used in modern speech</li>
<li class="d-docsite--list-element">They’re regional (i.e., “n’at”, “y’all”, “I’mma”)</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">You’ll receive a confirmation email shortly.</li>
<li class="d-docsite--list-element">You’re all set to go!</li>
<li class="d-docsite--list-element">Your playbook doesn’t have any topics.</li>
<li class="d-docsite--list-element">An error occurred and your updates couldn’t be saved.</li>
<li class="d-docsite--list-element">Your number hasn’t finished porting yet.</li>
<li class="d-docsite--list-element">You haven’t connected your account.</li>
<li class="d-docsite--list-element">You’ve reached your department limit.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">This action is possible if you’d update the application.</li>
<li class="d-docsite--list-element">There’re 100 people in this meeting.</li>
<li class="d-docsite--list-element">There are 10 people who’re in your room.</li>
<li class="d-docsite--list-element">It’d be a good idea to lock this call.</li>
<li class="d-docsite--list-element">Your number hasn’t finished porting yet.</li>
<li class="d-docsite--list-element">You gotta set up recurring credit payments to avoid platform disruption.</li>
<li class="d-docsite--list-element">That’ll be everything you need to do.</li>
</ul>
</template>
</dialtone-usage>
<h2 id="plain-language" tabindex="-1" class="d-docsite--header-2">Plain Language <a class="header-anchor d-link d-docsite--link d-link" href="#plain-language" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">When writing, communicate the way our customers do. Avoid using jargon or technical terminology. If you absolutely need to use it, clearly explain the concepts.</p>
<p class="d-docsite--paragraph">Sentences should have a single focus. Remember that we aim for an 8th grade reading level or lower in all communication.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Secure your account with additional security.</li>
<li class="d-docsite--list-element">Choose the language for your office phone number, voicemail, and voice menus.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Enable multi-factor authentication to ensure your account isn’t compromised.</li>
<li class="d-docsite--list-element">Choose the default main line number, IVR, and voicemail language.</li>
</ul>
</template>
</dialtone-usage>
<h2 id="capitalization" tabindex="-1" class="d-docsite--header-2">Capitalization <a class="header-anchor d-link d-docsite--link d-link" href="#capitalization" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We use <a href="#sentence-vs-title-casing" class="d-docsite--link d-link">sentence vs. title casing</a> for all writing except in the following situations:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Action-oriented areas (e.g. buttons and menus), which uses title casing</li>
<li class="d-docsite--list-element">Eyebrow headlines, which uses all caps casing</li>
</ul>
<h3 id="headings" tabindex="-1" class="d-docsite--header-3">Headings <a class="header-anchor d-link d-docsite--link d-link" href="#headings" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use sentence case for all headings. Capitalize the first word of each phrase, proper names, or trademarked nouns (i.e., branded product features).</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Create an office</li>
<li class="d-docsite--list-element">Calls to France are now even easier!</li>
<li class="d-docsite--list-element">Activate Ai Scorecards for this contact center</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Create an Office</li>
<li class="d-docsite--list-element">Calls to france are now even easier!</li>
<li class="d-docsite--list-element">Activate ai scorecards for this contact center</li>
</ul>
</template>
</dialtone-usage>
<h3 id="products-and-feature-names" tabindex="-1" class="d-docsite--header-3">Products and Feature Names <a class="header-anchor d-link d-docsite--link d-link" href="#products-and-feature-names" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">If a product or feature isn’t unique to Dialpad, such as contact centers, teams, or offices, then don’t capitalize the name. However, if it is unique to Dialpad and we can market it as a distinct product in the market, such as Ai Scorecards or Ai Playbooks, then capitalize it.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">teams</li>
<li class="d-docsite--list-element">Ai Scorecards</li>
<li class="d-docsite--list-element">main line number</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Teams</li>
<li class="d-docsite--list-element">ai scorecards</li>
<li class="d-docsite--list-element">Main Line Number</li>
</ul>
</template>
</dialtone-usage>
<h3 id="trademarks" tabindex="-1" class="d-docsite--header-3">Trademarks <a class="header-anchor d-link d-docsite--link d-link" href="#trademarks" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">At Dialpad we aim to respect the usage guidelines for any third-party intellectual party. Be sure to review each third party’s usage guidelines to ensure you’re using their intellectual property correctly.</p>
<p class="d-docsite--paragraph">For example, Salesforce does not allow their brand name to be used as part of a product offering such as “The Dialpad Salesforce app”. Instead state &quot;Dialpad integrates with Salesforce.&quot;</p>
<h3 id="job-titles" tabindex="-1" class="d-docsite--header-3">Job Titles <a class="header-anchor d-link d-docsite--link d-link" href="#job-titles" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Job titles should use title casing when placed close to a person’s name. When referring to a title without referencing a person, use sentence casing.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Jane Moreno, Staff Product Designer</li>
<li class="d-docsite--list-element">Staff Product Designer Jane Moreno</li>
<li class="d-docsite--list-element">Joy Davis is a senior software engineer.</li>
<li class="d-docsite--list-element">The senior software engineer coordinates multiple, strategic efforts across product teams.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Jane Moreno, staff product designer</li>
<li class="d-docsite--list-element">staff product designer Jane Moreno</li>
<li class="d-docsite--list-element">Jane Moreno is a Senior Software Engineer.</li>
<li class="d-docsite--list-element">The Senior Software Engineer coordinates multiple, strategic efforts across product teams.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="letters-following-slashes" tabindex="-1" class="d-docsite--header-3">Letters Following Slashes <a class="header-anchor d-link d-docsite--link d-link" href="#letters-following-slashes" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The first word following a slash shouldn’t be capitalized.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">ZIP/postal code required</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">ZIP/Postal code required</li>
</ul>
</template>
</dialtone-usage>
<h2 id="lists" tabindex="-1" class="d-docsite--header-2">Lists <a class="header-anchor d-link d-docsite--link d-link" href="#lists" aria-hidden="true">#</a></h2>
<h3 id="bulleted" tabindex="-1" class="d-docsite--header-3">Bulleted <a class="header-anchor d-link d-docsite--link d-link" href="#bulleted" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use a bulleted list when items are related, but the sequence or priority they are listed doesn’t matter.</p>
<p class="d-docsite--paragraph">Introduce lists with a full sentence and a colon. Capitalize the first word of a bullet even if the bulleted item is completing a sentence. If at least one bullet point requires periods due to having multiple sentences, include punctuation on all bullets.</p>
<p class="d-docsite--paragraph">Avoid lists with only one bullet point.</p>
<dialtone-usage>
<template #do>
<p class="d-docsite--paragraph">Using Ai Playbooks provides the following benefits:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Increased consistency to drive higher quality conversions</li>
<li class="d-docsite--list-element">Monitor your team’s calls in real-time</li>
<li class="d-docsite--list-element">Identify coachable moments afterwards with your team</li>
<li class="d-docsite--list-element">Create custom playbooks that aligns with your team’s unique needs</li>
</ul>
</template>
<template #dont>
<p class="d-docsite--paragraph">Using Ai Playbooks…</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">…increases consistency and drives higher quality conversions</li>
<li class="d-docsite--list-element">…you can monitor your team’s calls in real-time! Also you can review coachable moments later</li>
<li class="d-docsite--list-element">…allows you to create a custom playbook that aligns with your team’s unique needs</li>
</ul>
</template>
</dialtone-usage>
<h3 id="numbered" tabindex="-1" class="d-docsite--header-3">Numbered <a class="header-anchor d-link d-docsite--link d-link" href="#numbered" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use a numbered list when the sequence or priority order matters.</p>
<p class="d-docsite--paragraph">Introduce lists with a full sentence and a colon. Capitalize the first word of a bullet even if the bulleted item is completing a sentence. If at least one bullet point requires periods due to having multiple sentences, include punctuation on all bullets.</p>
<p class="d-docsite--paragraph">Avoid lists with only one bullet point.</p>
<dialtone-usage>
<template #do>
<p class="d-docsite--paragraph">To start a phone number porting request:</p>
<ol class="d-docsite--ordered-list">
<li class="d-docsite--list-element">Gather all the necessary information</li>
<li class="d-docsite--list-element">Create a new port request in your office’s settings</li>
<li class="d-docsite--list-element">Verify the request</li>
</ol>
</template>
<template #dont>
<p class="d-docsite--paragraph">To start a phone number porting request, you will need to:</p>
<ol class="d-docsite--ordered-list">
<li class="d-docsite--list-element">gather all the necessary information</li>
<li class="d-docsite--list-element">create a new port request in your office’s settings</li>
<li class="d-docsite--list-element">verify the request</li>
</ol>
</template>
</dialtone-usage>
<h3 id="menu-bars" tabindex="-1" class="d-docsite--header-3">Menu Bars <a class="header-anchor d-link d-docsite--link d-link" href="#menu-bars" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When space is limited, it's permissible to use horizontal action menus for the users. These menu items are often ordered based on logic, such as by popular actions or by greater to smaller options.</p>
<h4 class="d-docsite--header-4">Actions in a Menu</h4>
<p class="d-docsite--paragraph">Menu labels follow the same “Verb” + “Noun” pattern outlined in buttons. If there’s enough context, only a verb might be necessary.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Edit</li>
<li class="d-docsite--list-element">Add contact</li>
<li class="d-docsite--list-element">Copy</li>
<li class="d-docsite--list-element">Reply in thread</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Edit contact name</li>
<li class="d-docsite--list-element">add</li>
<li class="d-docsite--list-element">Copy Message Text</li>
<li class="d-docsite--list-element">Reply</li>
</ul>
</template>
</dialtone-usage>
<h4 class="d-docsite--header-4">Nouns in a Menu</h4>
<p class="d-docsite--paragraph">Nouns should be concise, but give users enough information that they can easily identify and select the desired item.</p>
<dialtone-usage>
<template #do>
<p class="d-docsite--paragraph">Time range filter</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Today</li>
<li class="d-docsite--list-element">Last week</li>
<li class="d-docsite--list-element">Last month</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Filter today</li>
<li class="d-docsite--list-element">Filter last week</li>
<li class="d-docsite--list-element">Filter from</li>
<li class="d-docsite--list-element">Filter call direction</li>
</ul>
</template>
</dialtone-usage>
<h2 id="dates-and-numbers" tabindex="-1" class="d-docsite--header-2">Dates and Numbers <a class="header-anchor d-link d-docsite--link d-link" href="#dates-and-numbers" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We attempt to properly localize dates, numbers, and measurements. These guidelines are for manually formatting in American English, which we use as the base before translating to other languages.</p>
<h3 id="dates" tabindex="-1" class="d-docsite--header-3">Dates <a class="header-anchor d-link d-docsite--link d-link" href="#dates" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Whenever possible, fully write out dates. Dates are always represented in this order:</p>
<blockquote>
<p class="d-docsite--paragraph">Weekday, Month DD, YYYY</p>
</blockquote>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">If there are space constraints, weekday and month names can be reduced to three characters. You must abbreviate both the weekday and month if both are shown.</li>
<li class="d-docsite--list-element">Use commas to separate the weekday, month and numerical day, and year.</li>
<li class="d-docsite--list-element">Include all 4 digits in the year.</li>
<li class="d-docsite--list-element">Omit the year if the date is older than one year from the date.</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Friday, July 13</li>
<li class="d-docsite--list-element">June 13, 2023</li>
<li class="d-docsite--list-element">Feb 20, 2020</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Thur, July 13, 2024</li>
<li class="d-docsite--list-element">June 13, ‘23</li>
<li class="d-docsite--list-element">2/20/20</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Don’t use ordinal indicators, which are words representing position or rank in a sequential order (1st, 2nd, 3rd, etc).</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">February 1–April 30</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">February 1st–April 30th</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Avoid writing dates numerically. We don’t localize dates and times currently, standardizing all numerical dates with American English format. Dates can be written numerically when displayed in a table or extremely tight spaces. Use the MM/DD/YY format.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">09/02/22</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">9/2/22</li>
<li class="d-docsite--list-element">2/9/22</li>
</ul>
</template>
</dialtone-usage>
<h3 id="time" tabindex="-1" class="d-docsite--header-3">Time <a class="header-anchor d-link d-docsite--link d-link" href="#time" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Currently all time formats are formatted using the following guidelines:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use a 12-hour clock, followed by “am” or “pm”.</li>
<li class="d-docsite--list-element">Include a space after the last number. For example, “Your call finished at 1:06 pm.” Adding the space helps with formatting with English-speaking markets outside of North America.</li>
<li class="d-docsite--list-element">Use the browser time of the logged in user.</li>
<li class="d-docsite--list-element">To show a time range, use an en dash and include the am/pm after both times, for example, 3:00 pm–4:00 pm.</li>
<li class="d-docsite--list-element">If indicating both the date and time, separate them with the word “at” instead of a comma.</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Thursday, September 2, 2022 at 12:45 pm</li>
<li class="d-docsite--list-element">September 2 at 12:45 pm</li>
<li class="d-docsite--list-element">Sep 2 at 12:45 pm</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Thu, September 2, 2022, 12:45 pm</li>
<li class="d-docsite--list-element">September 2 at 12:45PM</li>
<li class="d-docsite--list-element">Sep 2 @ 12:45 PM</li>
</ul>
</template>
</dialtone-usage>
<h3 id="time-zones" tabindex="-1" class="d-docsite--header-3">Time Zones <a class="header-anchor d-link d-docsite--link d-link" href="#time-zones" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Include a time zone only if it’s necessary.</li>
<li class="d-docsite--list-element">Use the time zone’s abbreviation following the am/pm, for example, 12:36 pm PT.</li>
<li class="d-docsite--list-element">If users are in a single time zone, don’t include an S (for Standard) or D (for Daylight). For example, 3:15 pm PT.</li>
<li class="d-docsite--list-element">If users are a combination of multiple time zones, then in the S (for Standard) or D (for Daylight). For example, 3:15 pm PDT.</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Thursday, September 2, 2022 at 12:45 pm EDT</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">2022–10-02, 2:00 P.M. EDT</li>
</ul>
</template>
</dialtone-usage>
<h3 id="timestamps" tabindex="-1" class="d-docsite--header-3">Timestamps <a class="header-anchor d-link d-docsite--link d-link" href="#timestamps" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">We use both relative and absolute timestamps, determining what to use based on content and context.</p>
<h4 class="d-docsite--header-4">Relative Timestamps</h4>
<p class="d-docsite--paragraph">Relative timestamps are best used in situations where specific timestamps would be tedious and unnecessary. Instead use relative timestamps when only trying to roughly communicate when something happened.</p>
<p class="d-docsite--paragraph">When using relative timestamps, use these formats:</p>
<table class="d-table">
  <thead>
    <tr>
      <th scope="col">Duration</th>
      <th scope="col" style="width: calc(100% / 3);">Format</th>
      <th scope="col" style="width: calc(100% / 3);">Example</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Less than 60 seconds</td>
      <td></td>
      <td>Just now</td>
    </tr>
    <tr>
      <td>Less than 60 minutes</td>
      <td>[ N ] minutes ago</td>
      <td>5 minutes ago</td>
    </tr>
    <tr>
      <td>Less than 24 hours</td>
      <td>[ N ] hours ago</td>
      <td>5 hours ago</td>
    </tr>
    <tr>
      <td>Less than 24 hours</td>
      <td>[ N ] hours ago</td>
      <td>5 hours ago</td>
    </tr>
    <tr>
      <td>Less than 48 hours</td>
      <td></td>
      <td>Yesterday</td>
    </tr>
    <tr>
      <td>Less than 1 week</td>
      <td>[ Weekday ]</td>
      <td>Thursday</td>
    </tr>
  </tbody>
</table>
<h4 class="d-docsite--header-4">Absolute Timestamps</h4>
<p class="d-docsite--paragraph">Absolute timestamps are used in situations where specific timestamps are important and necessary such as with call times, data tables, and historical records.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Spell out hours, minutes, and seconds whenever possible</li>
<li class="d-docsite--list-element">If necessary, they may be abbreviated:
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">hours - h</li>
<li class="d-docsite--list-element">minutes - m</li>
<li class="d-docsite--list-element">seconds - s</li>
</ul>
</li>
</ul>
<p class="d-docsite--paragraph">When using absolute timestamps, use these formats:</p>
<table class="d-table">
  <thead>
    <tr>
      <th scope="col">Duration</th>
      <th scope="col" style="width: calc(100% / 3);">Format</th>
      <th scope="col" style="width: calc(100% / 3);">Example</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Less than 1 week</td>
      <td>[ Weekday ]</td>
      <td>Thursday</td>
    </tr>
    <tr>
      <td>Less than 1 year</td>
      <td>[ Weekday ], [ Mon (abbrev if necessary) ] [ Day ]</td>
      <td>Thursday, October 12</td>
    </tr>
    <tr>
      <td>Equal to or greater than 1 year</td>
      <td>[ Mon (abbrev if necessary) ] [ Day ], [ Year ]</td>
      <td>May 16, 2023</td>
    </tr>
  </tbody>
</table>
<h4 class="d-docsite--header-4">Durations</h4>
<p class="d-docsite--paragraph">For calls, meetings, or any other session-based interaction, we show the duration amount. Both duration groupings (“hours”, “minutes”, “seconds”) and specific duration values should be separated by <a href="https://practicaltypography.com/nonbreaking-spaces.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">non-breaking spaces</a>.</p>
<table class="d-table">
  <thead>
    <tr>
      <th scope="col">Available Space</th>
      <th scope="col" style="width: calc(100% / 3);">Format</th>
      <th scope="col" style="width: calc(100% / 3);">Example</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>
        <p class="d-mb2"><b>Maximum space</b></p>
        <p>Write out the time identifiers fully when space allows.</p>
      </td>
      <td>[ NN hour(s) NN minutes ]</td>
      <td>1 hour 34 minutes</td>
    </tr>
    <tr>
      <td>
        <p class="d-mb2"><b>Limited space</b></p>
        <p>When space is somewhat limited, abbreviate the time identifiers.</p>
      </td>
      <td>[ NN hr NN min ]</td>
      <td>1 hr 34 min</td>
    </tr>
    <tr>
      <td>
        <p class="d-mb2"><b>Minimal space</b></p>
        <p>For tabular data instances, durations can be represented in absolute numerical values. Leading zeros must be present.</p>
      </td>
      <td>[ DD : HH : MM : SS ]</td>
      <td>01:04:05:30</td>
    </tr>
  </tbody>
</table>
<p class="d-docsite--paragraph">Use common day, hour, minute, and second amounts when rendering durations (i.e., “1 hr 2 min” versus “62 min”).</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">1 hr 2 min</li>
<li class="d-docsite--list-element">1 min 30 sec</li>
<li class="d-docsite--list-element">1 day 1 hour 1 min</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">62 min</li>
<li class="d-docsite--list-element">90 sec</li>
<li class="d-docsite--list-element">25 hr 1 min</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">When adding a duration after a time range, wrap the duration amount in paranthesis. Since the duration is secondary information to the time range, use a muted foreground color to visually diminish its importance.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">4:01 pm–5:02 pm <span class="d-fc-muted">(1 hr 2 min)</span></li>
<li class="d-docsite--list-element">11:27 am–11:28 am <span class="d-fc-muted">(1 min 30 sec)</span></li>
<li class="d-docsite--list-element">Monday 11:00 am–Tuesday 12:01 pm <span class="d-fc-muted">(1 day 1 hour 1 min)</span></li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">4:01 pm–5:02 pm (62 min)</li>
<li class="d-docsite--list-element">11:27 am–11:28 am for 90 sec</li>
<li class="d-docsite--list-element">Monday 11:00 am–Tuesday 12:01 pm, 25 hr 1 min</li>
</ul>
</template>
</dialtone-usage>
<h3 id="currency" tabindex="-1" class="d-docsite--header-3">Currency <a class="header-anchor d-link d-docsite--link d-link" href="#currency" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Only show a currency amount when showing a price that differs from the user’s normal currency (i.e., showing USD pricing to CAD customers or vice versa).</li>
<li class="d-docsite--list-element">When rendering prices, the currency always follows the dollar amount.</li>
<li class="d-docsite--list-element">Don’t include the cents digits (“.00”) or whole dollar amounts unless it is grouped with other amounts which have non-zero digit amounts.</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">$25 USD</li>
<li class="d-docsite--list-element">£ 38.27 GBP</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">$25.00 USD</li>
<li class="d-docsite--list-element">$25,000USD</li>
<li class="d-docsite--list-element">$1KUSD</li>
<li class="d-docsite--list-element">GBP £ 38.27</li>
</ul>
</template>
</dialtone-usage>
<h3 id="numbers" tabindex="-1" class="d-docsite--header-3">Numbers <a class="header-anchor d-link d-docsite--link d-link" href="#numbers" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Whenever possible use numerals. If the number is below 10 and not integral to the sentence, you may spell it out in full.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">12</li>
<li class="d-docsite--list-element">100</li>
<li class="d-docsite--list-element">1</li>
<li class="d-docsite--list-element">Here are 2 phone numbers you can use.</li>
<li class="d-docsite--list-element">You have 35 unread messages.</li>
<li class="d-docsite--list-element">You no longer need to read messages in three places.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">nine</li>
<li class="d-docsite--list-element">one hundred</li>
<li class="d-docsite--list-element">one</li>
<li class="d-docsite--list-element">Here are two phone numbers you can use.</li>
<li class="d-docsite--list-element">You have thirty-five unread messages.</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use commas for numbers with four or more digits. Whenever possible, don’t truncate numbers.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">9,865</li>
<li class="d-docsite--list-element">89,865</li>
<li class="d-docsite--list-element">1,089,865</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">9865</li>
<li class="d-docsite--list-element">89k</li>
<li class="d-docsite--list-element">1.09 m</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Our default phone number format for designs is the North American format. In Dialpad we localize phone number formats for each country. Format consistency helps our customers quickly identify similar numbers.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Prepend country codes with a plus sign</li>
<li class="d-docsite--list-element">For North American numbers, wrap the area code in parentheses and hyphenate the first 3 and last 4 digits</li>
<li class="d-docsite--list-element">For European numbers, separate the area code and subscriber number with a space</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">(415) 555-1234</li>
<li class="d-docsite--list-element">+1 (800) 555-1234</li>
<li class="d-docsite--list-element">+33 055 512345</li>
<li class="d-docsite--list-element">Ext. 55555</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">415.555.1234</li>
<li class="d-docsite--list-element">18005551234</li>
<li class="d-docsite--list-element">33 05 55 12 345</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use an en dash without surrounding spaces between number ranges.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">May 27–30</li>
<li class="d-docsite--list-element">1:33 pm–2:28 pm</li>
<li class="d-docsite--list-element">5–10 licenses</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">May 27 – 30</li>
<li class="d-docsite--list-element">1:33 pm – 2:28 pm</li>
<li class="d-docsite--list-element">5 – 10 licenses</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use “to” instead of an en dash if a number range is preceded by “from” in a phrase. Use “and” if a range is preceded by “between.”</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Active from May 1 to May 15</li>
<li class="d-docsite--list-element">Available between 1 pm and 3 pm</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Active from May 1–May 15</li>
<li class="d-docsite--list-element">Available between 1–3pm</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">For undefined upward ranges, use “and up” or “or more.”</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">$80 and up</li>
<li class="d-docsite--list-element">10 or more</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">$80+</li>
<li class="d-docsite--list-element">
<blockquote>
<p class="d-docsite--paragraph">10</p>
</blockquote>
</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">For downward ranges, be specific.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">$5–$25</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">$25 or less</li>
</ul>
</template>
</dialtone-usage>
<h2 id="places" tabindex="-1" class="d-docsite--header-2">Places <a class="header-anchor d-link d-docsite--link d-link" href="#places" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use a nation’s proper name when referring to it as a noun.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Your office is located in the United States.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Your office is located in the US.</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">When using a country as an adjective (such as when referring to currency), use the abbreviated form without punctuation.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">You have 10 unassigned UK phone numbers.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">You have 10 unassigned U.K. phone numbers.</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Be sure to not use colloquial forms of a nation or state name, for example, America instead of the United States. Also verify if a nation commonly uses “the” before their name, for example, “the Philippines.”</p>
<h2 id="punctuation" tabindex="-1" class="d-docsite--header-2">Punctuation <a class="header-anchor d-link d-docsite--link d-link" href="#punctuation" aria-hidden="true">#</a></h2>
<h3 id="ampersands" tabindex="-1" class="d-docsite--header-3">Ampersands <a class="header-anchor d-link d-docsite--link d-link" href="#ampersands" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Don’t use ampersands (&amp;). They attract attention to the least important part of the sentence. Instead spell out the word “and.”</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Taxes and fees</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Taxes &amp; fees</li>
</ul>
</template>
</dialtone-usage>
<h3 id="apostrophes" tabindex="-1" class="d-docsite--header-3">Apostrophes <a class="header-anchor d-link d-docsite--link d-link" href="#apostrophes" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use apostrophes to represent omitted letters or numbers:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Omitted numbers (‘20s)</li>
<li class="d-docsite--list-element">Omitted letters (don’t, can’t, isn’t)</li>
<li class="d-docsite--list-element">Verb contractions (it’s, you’re, they’re)</li>
</ul>
<p class="d-docsite--paragraph">Use apostrophes to form possessives:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Singular nouns: add <em>’s</em>, even if they end in <em>s</em></li>
<li class="d-docsite--list-element">Plural nouns that don’t end in <em>s</em>: add <em>’s</em></li>
<li class="d-docsite--list-element">Plural nouns that end in <em>s</em>: add an apostrophe</li>
</ul>
<p class="d-docsite--paragraph">Don’t use apostrophes to form possessive pronouns such as hers or his.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Office’s settings</li>
<li class="d-docsite--list-element">Customers’ credit cards</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Offices settings</li>
<li class="d-docsite--list-element">Customers credit cards</li>
</ul>
</template>
</dialtone-usage>
<h3 id="colons" tabindex="-1" class="d-docsite--header-3">Colons <a class="header-anchor d-link d-docsite--link d-link" href="#colons" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Avoid using colons in sentences. If you need to use one, don’t capitalize the first word after the colon unless it’s a proper noun.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Dialpad offers phone numbers in the United States, Canada, and Australia.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Dialpad offers phone numbers in: the United States, Canada, and Australia.</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Don’t use colons to introduce radio buttons or checkboxes.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Routed calls will be sent</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Routed calls will be sent:</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use colons to introduce bulleted lists.</p>
<dialtone-usage>
<template #do>
<p class="d-docsite--paragraph">Correct the following payment information to continue:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The credit card verification value (CVV) doesn’t match the credit card</li>
<li class="d-docsite--list-element">The billing address ZIP or postal code doesn’t match the credit card address</li>
</ul>
</template>
<template #dont>
<p class="d-docsite--paragraph">Correct the following payment information to continue.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The credit card verification value (CVV) doesn’t match the credit card</li>
<li class="d-docsite--list-element">The billing address ZIP or postal code doesn’t match the credit card address</li>
</ul>
</template>
</dialtone-usage>
<h3 id="commas" tabindex="-1" class="d-docsite--header-3">Commas <a class="header-anchor d-link d-docsite--link d-link" href="#commas" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the oxford comma (also known as the serial comma) in sentences. There should be a comma after every list of 3 or more items (unless you’re using a bulleted or numbered list).</p>
<dialtone-usage>
<template #do>
<p class="d-docsite--paragraph">Dialtone is a design system that outlines content writing guidelines, design foundations, and implementation guidance.</p>
</template>
<template #dont>
<p class="d-docsite--paragraph">Dialtone is a design system that outlines content writing guidelines, design foundations and implementation guidance.</p>
</template>
</dialtone-usage>
<h3 id="ellipses" tabindex="-1" class="d-docsite--header-3">Ellipses <a class="header-anchor d-link d-docsite--link d-link" href="#ellipses" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use an ellipsis (…) in place of missing text (or portions of it) such as truncation in a recap summary or user status messages. Don’t use ellipses for placeholder copy or to trail off a sentence.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Search calls</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Search calls…</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Always use the ellipsis character, not three periods.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">… <dt-keyboard-shortcut shortcut="Option+;"/></li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">...</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Consider space constraints in the interface when deciding to use truncation. Identify the portion of the text strings users need the most. It’s usually the beginning or the end. If both are important, consider truncating the middle of the string.</p>
<p class="d-docsite--paragraph">A button with an ellipsis icon (not the same as the text) is used to expand secondary actions. It’s typically used on table rows or with horizontal action sets when space is limited.</p>
<h3 id="en-dashes-and-em-dashes" tabindex="-1" class="d-docsite--header-3">En-Dashes and Em-Dashes <a class="header-anchor d-link d-docsite--link d-link" href="#en-dashes-and-em-dashes" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use an en dash with no spaces in between (–) for a fixed range of numbers.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">2020–2024</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">2020 – 2024</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use an em dash only if you can’t make your message clearer by splitting it into two sentences. Use an em dash without a space on either side (—).</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Customize your chatbot theme’s colors, typography, and loading screen—all in one place.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Customize your chatbot theme—colors, typography, and loading screen—all in one place.</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">On Mac the keyboard shortcuts for these punctuation marks are:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">En dash: <dt-keyboard-shortcut shortcut="Option+-"/></li>
<li class="d-docsite--list-element">Em dash: <dt-keyboard-shortcut shortcut="Shift+Option+-"/></li>
</ul>
<h3 id="exclamation-marks" tabindex="-1" class="d-docsite--header-3">Exclamation Marks <a class="header-anchor d-link d-docsite--link d-link" href="#exclamation-marks" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Avoid using exclamation marks. Most of our copy is rarely that exciting. If you absolutely have to, limit yourself to one exclamation mark to a page.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Your chatbot is ready to deploy!</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">You’ve updated your contact center title!</li>
</ul>
</template>
</dialtone-usage>
<h3 id="hyphens" tabindex="-1" class="d-docsite--header-3">Hyphens <a class="header-anchor d-link d-docsite--link d-link" href="#hyphens" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use hyphens to form compound modifiers, which are two words that combine to modify or describe the noun that follows.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Start your free, no-risk, 14-day trial today.</li>
<li class="d-docsite--list-element">Purchase a toll-free number.</li>
<li class="d-docsite--list-element">Log into your account.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Start your free, no risk, 14 day trial.</li>
<li class="d-docsite--list-element">Purchase a toll free number.</li>
<li class="d-docsite--list-element">Log-in to your account</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Join prefixes and suffixes only if there are two vowels beside each other. Never use hyphens in the word &quot;email.&quot;</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Re-open</li>
<li class="d-docsite--list-element">Resend</li>
<li class="d-docsite--list-element">Sign up</li>
<li class="d-docsite--list-element">Log in</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Reopen</li>
<li class="d-docsite--list-element">Re-send</li>
<li class="d-docsite--list-element">Sign-up</li>
<li class="d-docsite--list-element">Login</li>
</ul>
</template>
</dialtone-usage>
<h3 id="periods" tabindex="-1" class="d-docsite--header-3">Periods <a class="header-anchor d-link d-docsite--link d-link" href="#periods" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Periods can often end up in places they shouldn’t or be omitted incorrectly. In general, don’t use periods in interface copy unless it’s a full sentence description.</p>
<p class="d-docsite--paragraph">Use periods for:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Complete sentences</li>
<li class="d-docsite--list-element">Body text, descriptions, and subtitles</li>
<li class="d-docsite--list-element">Help text under input fields</li>
</ul>
<p class="d-docsite--paragraph">Don't use periods for:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Sentence fragments</li>
<li class="d-docsite--list-element">Top-level headings or page titles</li>
<li class="d-docsite--list-element">Buttons</li>
<li class="d-docsite--list-element">Notifications</li>
<li class="d-docsite--list-element">Toast messages</li>
<li class="d-docsite--list-element">Placeholder copy</li>
<li class="d-docsite--list-element">Navigation menu items</li>
<li class="d-docsite--list-element">Hover/tooltip text</li>
<li class="d-docsite--list-element">Radio button and checkbox text</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Placeholder example:</strong> Search Dialpad</li>
<li class="d-docsite--list-element"><strong>Description example:</strong> Mask your outbound phone number while keeping the call associated with your account. If you are sending a message, your number will not be masked.</li>
<li class="d-docsite--list-element"><strong>Button example:</strong> Create Scorecard</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Placeholder example:</strong> Search…</li>
<li class="d-docsite--list-element"><strong>Description example:</strong> Mask your outbound phone number while keeping the call associated with your account</li>
<li class="d-docsite--list-element"><strong>Button example:</strong> Create Scorecard.</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">You may use periods in bulleted, numbered, or link lists when a list item has two or more full sentences.</p>
<h3 id="question-marks" tabindex="-1" class="d-docsite--header-3">Question Marks <a class="header-anchor d-link d-docsite--link d-link" href="#question-marks" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Avoid using question marks wherever possible. Reword sentences into affirmative statements wherever you can, but there are exceptions.</p>
<p class="d-docsite--paragraph">Use a question mark if you don’t know the result of a question:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">“Did you forget your password?”</li>
<li class="d-docsite--list-element">“Are you porting Canadian, Hawaiian, toll-free, or international numbers?”</li>
</ul>
<p class="d-docsite--paragraph">Don't use question marks if:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">It's the only option available (e.g. &quot;Reset password&quot;)</li>
<li class="d-docsite--list-element">It's an on/off option (e.g. show/hide input)</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Forgot?</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Reset password?</li>
</ul>
</template>
</dialtone-usage>
<h3 id="quotation-marks" tabindex="-1" class="d-docsite--header-3">Quotation Marks <a class="header-anchor d-link d-docsite--link d-link" href="#quotation-marks" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use quotation marks to define words (“agents”) or to quote text. Use quotation marks to indicate input that the user has provided such as a title or file name. Use this indication only when the input appears in running text.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Are you sure you want to delete “Leap Year” holiday?</li>
<li class="d-docsite--list-element">“css-pain.gif” failed to upload</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Are you sure you want to delete Leap Year holiday?</li>
<li class="d-docsite--list-element">css-pain.gif failed to upload</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Place commas and periods inside quotation marks. When working with literal strings like typed commands or user inputs, place the punctuation outside quotation marks.</p>
<p class="d-docsite--paragraph">When referring to specific UI areas, wrap the title in quotation marks.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid general words like “all”, “every”, or “most.”</li>
<li class="d-docsite--list-element">To remove this contact center, type “CONTACT CENTER”.</li>
<li class="d-docsite--list-element">Select “Advanced settings”</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid general words like “all”, “every”, or “most”.</li>
<li class="d-docsite--list-element">To remove this contact center, type “CONTACT CENTER.”</li>
<li class="d-docsite--list-element">Select Advanced Settings</li>
</ul>
</template>
</dialtone-usage>
<h3 id="semicolons" tabindex="-1" class="d-docsite--header-3">Semicolons <a class="header-anchor d-link d-docsite--link d-link" href="#semicolons" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Avoid semicolons if possible. If you need to use them, use them only to connect two closely related ideas, as long as they are both independent clauses (full sentences that could stand on their own). Also you can use semicolons to replace a comma or the word “and” between two closely related ideas.</p>
<dialtone-usage>
<template #do>
<p class="d-docsite--paragraph">The design team continues to grow; we’re improving daily.</p>
</template>
<template #dont>
<p class="d-docsite--paragraph">The design team continues to grow; the rain is starting.</p>
</template>
</dialtone-usage>
<h2 id="spelling-and-formatting" tabindex="-1" class="d-docsite--header-2">Spelling and Formatting <a class="header-anchor d-link d-docsite--link d-link" href="#spelling-and-formatting" aria-hidden="true">#</a></h2>
<h3 id="american-spelling" tabindex="-1" class="d-docsite--header-3">American Spelling <a class="header-anchor d-link d-docsite--link d-link" href="#american-spelling" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use American spelling for all external-facing Dialpad content. When in doubt, review the <a href="https://www.merriam-webster.com/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Merriam-Webster dictionary</a> for the preferred word spelling.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Color</li>
<li class="d-docsite--list-element">Center</li>
<li class="d-docsite--list-element">Canceled</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Colour</li>
<li class="d-docsite--list-element">Centre</li>
<li class="d-docsite--list-element">Cancelled</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">It’s easy to miss Canadian spelling. Switch your computer’s language settings to American English and turn spell check on. It will highlight words you might miss.</p>
<h3 id="bold" tabindex="-1" class="d-docsite--header-3">Bold <a class="header-anchor d-link d-docsite--link d-link" href="#bold" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When in doubt, don’t bold copy. We use bold sparingly and only when strong emphasis is required. Don’t use bold to artificially create a heading or emphasize proper nouns, user input, or checkbox titles.</p>
<dialtone-usage>
<template #do>
<p class="d-docsite--paragraph">We’ve sent a confirmation email to <a href="mailto:user@domain.com" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">user@domain.com</a>.</p>
</template>
<template #dont>
<p class="d-docsite--paragraph">We’ve sent a confirmation email to <strong><a href="mailto:user@domain.com" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">user@domain.com</a></strong>.</p>
</template>
</dialtone-usage>
<h2 id="you-we-and-other-personal-pronouns" tabindex="-1" class="d-docsite--header-2">You, We, and Other Personal Pronouns <a class="header-anchor d-link d-docsite--link d-link" href="#you-we-and-other-personal-pronouns" aria-hidden="true">#</a></h2>
<h3 id="addressing-users" tabindex="-1" class="d-docsite--header-3">Addressing Users <a class="header-anchor d-link d-docsite--link d-link" href="#addressing-users" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Always refer to users as “you.” Don’t speak for users with phrases that use “I” or “my.”</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Change your profile picture</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Change my profile picture</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">In some cases, such as when having users consent or grant permissions, you should refer to users as “I.”</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">I agree to the terms and conditions.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">You agree to the terms and conditions.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="referring-to-dialpad" tabindex="-1" class="d-docsite--header-3">Referring to Dialpad <a class="header-anchor d-link d-docsite--link d-link" href="#referring-to-dialpad" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Always refer to Dialpad as “we,” but avoid inserting Dialpad into content as much as possible (except when a human is taking an action, such as reviewing a request).</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add your team members</li>
<li class="d-docsite--list-element">Integrate with the tools you use every day</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add your team members to Dialpad</li>
<li class="d-docsite--list-element">We offer integrations to the tools you use every day</li>
</ul>
</template>
</dialtone-usage>
</div></template>


